/**
 * AdminTopBar - Top bar reused by sidebar/compact layouts.
 */
import React from "react";
import { Menu, ChevronRight, Sun, Moon, Search as SearchIcon, Sparkles, Bot, Monitor } from "lucide-react";
import { useNavigate } from "react-router-dom";
import NotificationBell from "../../../components/NotificationBell";
import TopbarChatButton from "../../../components/TopbarChatButton";
import WebPushToggle from "./WebPushToggle";
import { LayoutPickerButton, SpacingDensityPicker } from "./LayoutPickers";
import { openAdvisor } from "../../../hooks/useAIAdvisor";

export function AdminTopBar({
  onToggleSidebar, layout, onLayoutChange, showLayoutPicker, setShowLayoutPicker,
  isDark, toggleTheme, spacingDensity, onSpacingChange, onOpenSearch,
}) {
  const navigate = useNavigate();
  return (
    <header className="glass-topbar px-2 sm:px-4 h-12 sm:h-14 flex items-center gap-1.5 sm:gap-4 flex-shrink-0 relative z-20">
      <button onClick={onToggleSidebar} className="lg:hidden text-slate-500 hover:text-slate-800 p-1.5 rounded-lg hover:bg-slate-100/80 transition-colors" data-testid="sidebar-toggle">
        <Menu className="w-5 h-5" />
      </button>
      {onOpenSearch && (
        <button onClick={onOpenSearch}
          className="lg:hidden flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-full text-sm text-slate-400 bg-slate-100/60 hover:bg-slate-100 border border-slate-200/50 transition-colors"
          data-testid="topbar-search-btn">
          <SearchIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline text-xs">Hledat...</span>
          <kbd className="hidden md:flex text-[9px] font-mono bg-white/80 border border-slate-200 px-1.5 py-0.5 rounded text-slate-400">&#8984;K</kbd>
        </button>
      )}
      <div className="flex-1" />
      {/* iter413g — Twin AI buttons: Agent (full-page) + Radce (drawer with page context).
       *  At narrower viewports (<1024px / lg breakpoint) we hide labels and only show icons
       *  to make room for notifications/search on laptops. */}
      <div className="hidden sm:flex items-center bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-full p-0.5 shadow-sm">
        <button onClick={() => navigate("/admin/ai-agent")}
          data-testid="topbar-ai-agent-btn"
          title="AI Agent — celostránkový asistent s historií konverzace"
          className="flex items-center gap-1.5 px-2 lg:px-2.5 py-1 rounded-full text-xs font-medium text-white hover:bg-white/10 transition-colors">
          <Bot className="w-3.5 h-3.5" />
          <span className="hidden xl:inline">Agent</span>
        </button>
        <div className="w-px h-4 bg-white/20" />
        <button onClick={() => openAdvisor()}
          data-testid="topbar-ai-advisor-btn"
          title="AI Radce — rychlý drawer s kontextem stránky (⌘+I)"
          className="flex items-center gap-1.5 px-2 lg:px-2.5 py-1 rounded-full text-xs font-medium text-white hover:bg-white/10 transition-colors">
          <Sparkles className="w-3.5 h-3.5" />
          <span className="hidden xl:inline">Radce</span>
          <kbd className="hidden 2xl:flex text-[9px] font-mono bg-white/20 px-1 py-0.5 rounded text-white/90">&#8984;I</kbd>
        </button>
      </div>
      <button onClick={() => openAdvisor()}
        data-testid="topbar-ai-advisor-btn-mobile"
        title="AI Radce"
        className="sm:hidden p-1.5 rounded-lg text-white bg-gradient-to-br from-violet-600 to-fuchsia-600">
        <Sparkles className="w-4 h-4" />
      </button>
      <NotificationBell />
      <TopbarChatButton />
      <WebPushToggle />
      <div className="hidden sm:block">
        <SpacingDensityPicker density={spacingDensity} onChange={onSpacingChange} />
      </div>
      <div className="hidden sm:block">
        <LayoutPickerButton layout={layout} onChange={onLayoutChange}
          showLayoutPicker={showLayoutPicker} setShowLayoutPicker={setShowLayoutPicker} />
      </div>
      <button onClick={toggleTheme}
        className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100/80 transition-colors"
        data-testid="theme-toggle-btn">
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
      {/* iter413ac — Toggle to admin2 desktop UI */}
      <button onClick={() => navigate("/admin2")}
        title="Přepnout do Admin2 (desktop UI)"
        data-testid="topbar-switch-admin2"
        className="text-slate-400 hover:text-violet-700 p-1.5 rounded-lg hover:bg-violet-50/80 transition-colors">
        <Monitor className="w-4 h-4" />
      </button>
      <a href="/" target="_blank" rel="noreferrer"
        className="hidden md:flex text-xs text-slate-500 hover:text-emerald-600 items-center gap-1 transition-colors px-2 py-1 rounded-full hover:bg-emerald-50/50">
        <span>Zobrazit formulář</span> <ChevronRight className="w-3 h-3" />
      </a>
    </header>
  );
}
